import React from "react";
import { Plane, Clock } from "lucide-react";
import { aircraftName } from "@/lib/aircraftNames";

function hhmm(iso) {
  if (!iso) return "—";
  return String(iso).slice(11, 16);
}
function dateOnly(iso) {
  if (!iso) return "";
  const d = String(iso).slice(0, 10).split("-");
  if (d.length === 3) return `${d[2]}/${d[1]}/${d[0]}`;
  return iso.slice(0, 10);
}
function durFromISO(s) {
  if (!s) return "";
  const m = /PT(?:(\d+)H)?(?:(\d+)M)?/.exec(s);
  if (!m) return s;
  const h = m[1] ? `${m[1]}h ` : "";
  const mm = m[2] ? `${m[2]}m` : "";
  return `${h}${mm}`.trim();
}
function layoverMinutes(prevArriveISO, nextDepartISO) {
  if (!prevArriveISO || !nextDepartISO) return 0;
  const a = new Date(prevArriveISO).getTime();
  const d = new Date(nextDepartISO).getTime();
  return Math.max(0, Math.round((d - a) / 60000));
}
function fmtMin(min) {
  if (!min) return "";
  const h = Math.floor(min / 60), m = min % 60;
  return [h ? `${h}h` : "", m ? `${m}m` : ""].filter(Boolean).join(" ");
}

/**
 * Vertical timeline of an itinerary in ONE compact card.
 * Each waypoint is a dot connected by a vertical line (solid = in flight,
 * dashed = layover). Shows times, dates, airport + terminal, carrier,
 * flight number, aircraft, per-leg duration and layover duration.
 */
export default function FlightSegmentTimeline({ flight }) {
  // Build a unified list of segments from whichever supplier shape the card has.
  let segs = [];
  if (flight?.amadeus_offer?.itineraries?.[0]?.segments) {
    segs = flight.amadeus_offer.itineraries[0].segments.map((s) => ({
      carrierCode: s.carrierCode,
      flightNumber: s.number,
      from: s.departure?.iataCode,
      fromTerminal: s.departure?.terminal,
      depAt: s.departure?.at,
      to: s.arrival?.iataCode,
      toTerminal: s.arrival?.terminal,
      arrAt: s.arrival?.at,
      aircraft: aircraftName(s.aircraft?.code || ""),
      duration: s.duration,
      technicalStops: s.numberOfStops || 0,
    }));
  } else if (Array.isArray(flight?.travelport_segments) && flight.travelport_segments.length) {
    segs = flight.travelport_segments.map((s) => ({
      carrierCode: s.Carrier,
      flightNumber: s.FlightNumber,
      from: s.Origin,
      depAt: s.DepartureTime,
      to: s.Destination,
      arrAt: s.ArrivalTime,
      aircraft: s.aircraft || aircraftName(s.Equipment || ""),
      bookingCode: s.BookingCode,
    }));
  }

  if (!segs.length) {
    return <div className="text-xs text-slate-500">No detailed segment data available for this fare.</div>;
  }

  // Build a flat list of timeline rows
  const rows = [];
  segs.forEach((s, i) => {
    const carrier = s.carrierCode || flight.airline_code;
    const num = s.flightNumber ? `${carrier}${s.flightNumber}` : flight.flight_number;
    rows.push({ kind: "waypoint", iata: s.from, terminal: s.fromTerminal, time: hhmm(s.depAt), date: dateOnly(s.depAt) });
    // duration may be ISO PT or absent — compute from times if missing
    let durStr = durFromISO(s.duration);
    if (!durStr && s.depAt && s.arrAt) {
      const m = Math.max(0, Math.round((new Date(s.arrAt) - new Date(s.depAt)) / 60000));
      durStr = fmtMin(m);
    }
    rows.push({
      kind: "flight", carrier, airlineName: flight.airline_name, flightNo: num,
      aircraft: s.aircraft, duration: durStr,
      bookingCode: s.bookingCode, technicalStops: s.technicalStops || 0,
    });
    rows.push({ kind: "waypoint", iata: s.to, terminal: s.toTerminal, time: hhmm(s.arrAt), date: dateOnly(s.arrAt) });

    const next = segs[i + 1];
    if (next) {
      const lo = layoverMinutes(s.arrAt, next.depAt);
      if (lo > 0) rows.push({ kind: "layover", duration: fmtMin(lo), iata: s.to });
    }
  });

  const totalMin = segs.reduce((acc, s) => {
    if (s.duration) return acc + (durFromISO(s.duration) ? 0 : 0) + 0; // ignore
    if (s.depAt && s.arrAt) return acc + Math.max(0, Math.round((new Date(s.arrAt) - new Date(s.depAt)) / 60000));
    return acc;
  }, 0);
  // Add layovers
  let totalWithLayovers = totalMin;
  for (let i = 0; i < segs.length - 1; i++) {
    totalWithLayovers += layoverMinutes(segs[i].arrAt, segs[i + 1].depAt);
  }
  const totalDur = fmtMin(totalWithLayovers) || (flight.duration_min ? fmtMin(flight.duration_min) : "");
  const stops = segs.length - 1;

  return (
    <div className="bg-slate-50/60 border border-slate-200 rounded-xl p-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3 pb-2.5 border-b border-slate-200/80">
        <div className="text-xs text-slate-500 inline-flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5 text-[var(--brand-primary)]" />
          Total journey: <span className="font-semibold text-[var(--brand-accent)]">{totalDur || "—"}</span>
          <span className="text-slate-300 mx-1">·</span>
          {stops === 0 ? "Non-stop" : `${stops} stop${stops > 1 ? "s" : ""}`}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
          {flight.from} → {flight.to}
        </div>
      </div>

      {/* Horizontal timeline */}
      <div className="overflow-x-auto">
        <div className="flex items-stretch min-w-max gap-0">
          {segs.map((s, i) => {
            const carrier = s.carrierCode || flight.airline_code;
            const num = s.flightNumber ? `${carrier}${s.flightNumber}` : flight.flight_number;
            let durStr = durFromISO(s.duration);
            if (!durStr && s.depAt && s.arrAt) {
              durStr = fmtMin(Math.max(0, Math.round((new Date(s.arrAt) - new Date(s.depAt)) / 60000)));
            }
            const layover = i < segs.length - 1 ? layoverMinutes(s.arrAt, segs[i + 1].depAt) : 0;
            return (
              <React.Fragment key={i}>
                {/* Origin station */}
                <div className="flex flex-col items-center text-center px-3 min-w-[110px]" data-testid={`tlh-from-${i}`}>
                  <div className="font-heading text-xl font-bold text-[var(--brand-accent)] tabular-nums leading-none">{hhmm(s.depAt)}</div>
                  <div className="font-mono text-sm font-bold text-slate-700 mt-1">{s.from}</div>
                  {s.fromTerminal && (
                    <span className="text-[10px] uppercase bg-slate-200 text-slate-700 px-1 py-0.5 rounded mt-0.5">T{s.fromTerminal}</span>
                  )}
                  <div className="text-[10px] text-slate-400 mt-0.5">{dateOnly(s.depAt)}</div>
                </div>

                {/* Flight bar */}
                <div className="flex flex-col items-center justify-center min-w-[170px] flex-1 px-1" data-testid={`tlh-flight-${i}`}>
                  <div className="text-[11px] text-slate-700 font-semibold leading-tight text-center">{flight.airline_name || carrier}</div>
                  <div className="relative w-full flex items-center my-1.5">
                    <span className="w-2 h-2 rounded-full bg-[var(--brand-primary)] flex-none" />
                    <span className="flex-1 h-0.5 bg-[var(--brand-primary)]" />
                    <Plane className="w-3.5 h-3.5 text-[var(--brand-primary)] -mx-1 rotate-90" />
                    <span className="flex-1 h-0.5 bg-[var(--brand-primary)]" />
                    <span className="w-2 h-2 rounded-full bg-[var(--brand-primary)] flex-none" />
                  </div>
                  <div className="text-[10px] text-slate-500 text-center">{num}{s.aircraft ? ` · ${s.aircraft}` : ""}</div>
                  <div className="text-[10px] text-slate-500 inline-flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> {durStr || "—"}</div>
                </div>

                {/* Destination station */}
                <div className="flex flex-col items-center text-center px-3 min-w-[110px]" data-testid={`tlh-to-${i}`}>
                  <div className="font-heading text-xl font-bold text-[var(--brand-accent)] tabular-nums leading-none">{hhmm(s.arrAt)}</div>
                  <div className="font-mono text-sm font-bold text-slate-700 mt-1">{s.to}</div>
                  {s.toTerminal && (
                    <span className="text-[10px] uppercase bg-slate-200 text-slate-700 px-1 py-0.5 rounded mt-0.5">T{s.toTerminal}</span>
                  )}
                  <div className="text-[10px] text-slate-400 mt-0.5">{dateOnly(s.arrAt)}</div>
                </div>

                {/* Layover chip */}
                {layover > 0 && (
                  <div className="flex items-center px-2" data-testid={`tlh-layover-${i}`}>
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full whitespace-nowrap">
                      <Clock className="w-3 h-3" /> {fmtMin(layover)} layover at <span className="font-mono font-bold">{s.to}</span>
                    </span>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
